import React from 'react';
import ReactDOM from 'react-dom/client';
import { RouterProvider } from 'react-router-dom';
import { HelmetProvider } from 'react-helmet-async';
import { router } from './router';
import { ToastProvider } from './components/Toast';
import { PodcastPlayerProvider } from './components/podcast/PodcastPlayerContext';
import PodcastPlayer from './components/PodcastPlayer';
import GlobalUploadPanel from './components/upload/GlobalUploadPanel';
import './i18n';
import { installLocaleHeader } from './lib/send-locale';
import './design/tokens.css';
import './globals.css';
import { getToken } from './utils/secureToken';
import { initRUM } from './lib/rum';
import { initPostHog, posthog } from './lib/posthog';
import { onAnalyticsConsentChange } from './lib/consent';
import AppErrorBoundary from './components/ErrorBoundary';
import AccountChangeGuard from './components/AccountChangeGuard';
import { reloadForFailedChunk } from './lib/chunkReload';
import { watchForNewBuild } from './lib/newVersion';
import { getPartnerSite, installPartnerSession } from './partner/site';
import PartnerApp from './partner/PartnerApp';

// Token sanity guard — purge garbage from localStorage at boot. We've seen
// the auth-token slot end up holding a stringified error response (e.g.
// `{"error":"Server error"}`), which then gets sent as `Authorization:
// Bearer {"error":"Server error"}` on every request. The API can't parse
// that as a JWT and the request fails. Any value that doesn't look like a
// JWT (three base64url segments separated by `.`) gets dropped so we fail
// closed to the login screen rather than looping on the bad token.
(function purgeBadAuthToken() {
  if (typeof window === 'undefined') return;
  const JWT_RE = /^[A-Za-z0-9_-]+\.[A-Za-z0-9_-]+\.[A-Za-z0-9_-]+$/;
  for (const key of ['authToken', 'soltik_auth_token', 'adminAuthToken', 'adminToken']) {
    const v = localStorage.getItem(key);
    if (v && !JWT_RE.test(v)) {
      console.warn(`[auth] purging non-JWT value at localStorage.${key}`);
      localStorage.removeItem(key);
    }
  }
})();

// Prime the legacy `authToken` mirror from the active session synchronously,
// before React renders. The multi-session store (utils/secureToken) only
// writes the legacy `authToken` key lazily on the first getToken() call. Call
// sites that read the raw key directly (DeviceContext, cosmetics, library
// folders, workspace stats, etc.) would otherwise fire their first authed
// fetch before that sync and 401 — succeeding only after a reload once the
// mirror is populated. getToken() reconciles the mirror (and purges expired
// tokens) up front, closing the race for every raw reader at once.
(function primeAuthTokenMirror() {
  if (typeof window === 'undefined') return;
  try { getToken(); } catch { /* fail closed to login */ }
})();

// Auto-recover from stale-bundle chunk-load errors. When a new deploy
// replaces old chunk hashes on the CDN, any SPA session that still
// has the old index.html cached will fail to import the new lazy
// chunks ("Failed to fetch dynamically imported module"). We reload
// once to pick up the fresh index.html + chunk URLs. A sessionStorage
// guard prevents reload loops if the new bundle ALSO fails.
(function installStaleBundleGuard() {
  if (typeof window === 'undefined') return;
  let reloaded = false;
  const reloadOnce = () => {
    if (reloaded) return;
    // Shares its attempt counter with the router's lazy() guard (lib/
    // chunkReload), so the two cannot take turns reloading the same broken
    // page forever. The old flag here expired after 30s, which let a
    // permanently unfetchable chunk reload the page twice a minute for as
    // long as the tab stayed open.
    // Only a reload that actually started counts — one deferred for running
    // uploads must be able to happen once they finish.
    reloaded = reloadForFailedChunk();
  };
  window.addEventListener('vite:preloadError', () => reloadOnce());
  watchForNewBuild();
  window.addEventListener('unhandledrejection', (e) => {
    const msg = String((e.reason as any)?.message || e.reason || '');
    if (/Failed to fetch dynamically imported module/i.test(msg)
        || /Loading chunk [\d]+ failed/i.test(msg)) {
      reloadOnce();
    }
  });
})();

// Kick off Real User Monitoring (Web Vitals + SPA nav timing)
initRUM();

// First-party behavioral session marker (lib/events batches to /api/events).
// First-party, no cookies, no cross-site tracking — unlike PostHog below it
// is not consent-gated, same as the rest of the first-party event pipeline.
import('./lib/events').then(({ trackSession }) => trackSession.start()).catch(() => {});

// PostHog — product analytics + error capture + session replay.
//
// Gated on consent, and deliberately NOT called unconditionally: PostHog
// sets a cookie and starts session replay the moment it initialises, so
// calling it before the visitor has agreed is the violation itself, not
// something a later opt-out repairs. If consent is withdrawn we stop
// capturing and clear what was stored.
onAnalyticsConsentChange((granted) => {
  if (granted) {
    initPostHog();
    try {
      posthog.opt_in_capturing();
      // Tag this build so PostHog groups errors by release + lets us see
      // which deploy introduced a regression. Registered HERE rather than at
      // module scope: register() on an uninitialised client is a silent
      // no-op, so at top level it would quietly stop tagging any release
      // the moment init moved behind consent.
      const version = (import.meta as any).env?.VITE_APP_VERSION || 'dev';
      posthog.register({ app_version: version });
    } catch { /* not yet loaded */ }
  } else {
    try {
      // ORDER MATTERS. reset() clears persisted state INCLUDING the opt-out
      // flag, so opting out first and resetting second silently re-enables
      // capturing. Verified in-browser: opt_out -> has_opted_out === true,
      // then reset() -> back to false. Reset first, then opt out last.
      posthog.reset(true);        // drops distinct_id + stored properties
      posthog.opt_out_capturing();
    } catch { /* never initialised — nothing to undo */ }
  }
});

// The inline HTML loading spinner (#app-loader) is dismissed automatically
// by a MutationObserver in index.html once #root has rendered content.
// No manual dismissal needed here — this avoids the previous black-screen
// gap where React mounted and hid the spinner before lazy routes resolved.

// Tell the API which language the user is reading, so the prose it generates
// for them — video concepts, narration that gets read aloud — comes back in it.
// Installed before anything can fetch.
installLocaleHeader();

// Service worker for PWA support — production builds only.
//
// It used to register under the Vite dev server too, putting a production
// caching layer (cache-first static assets, a precached shell) in front of
// modules that change on every save. The browser then showed a page that
// contradicted a fresh Playwright run of the same code — exactly when you are
// trying to verify a fix.
if ('serviceWorker' in navigator) {
  if (import.meta.env.PROD) {
    window.addEventListener('load', () => {
      navigator.serviceWorker.register('/sw.js').catch(() => {
        // Service worker registration failed — not critical, app works without it
      });
    });
  } else {
    // Every machine that ever ran dev still has the worker installed, and it
    // keeps intercepting until removed. Remove OURS only: localhost:3000 is
    // shared by any project that runs a dev server on that port.
    //
    // Takes two loads, by browser design: unregister() does not release the
    // page the worker already controls, and on that load it re-caches its own
    // responses after the delete below. From the next load the page is
    // uncontrolled and the delete sticks — and caches no worker reads are inert.
    navigator.serviceWorker.getRegistrations()
      .then((regs) => Promise.all(regs
        .filter((r) => {
          const url = (r.active || r.waiting || r.installing)?.scriptURL;
          return !!url && new URL(url).pathname === '/sw.js';
        })
        .map((r) => r.unregister())))
      .catch(() => { /* nothing to clean up */ });
    if ('caches' in window) {
      caches.keys()
        .then((keys) => Promise.all(keys.filter((k) => k.startsWith('soltik-')).map((k) => caches.delete(k))))
        .catch(() => { /* nothing to clean up */ });
    }
  }
}

// A verified partner domain (soltik-web injects it): render the partner app, whose
// token store mirrors the domain's session (partner/site.ts installPartnerSession).
const partnerSite = getPartnerSite();
if (partnerSite) installPartnerSession(partnerSite);

ReactDOM.createRoot(document.getElementById('root')!).render(
  <React.StrictMode>
    <AppErrorBoundary>
      <HelmetProvider>
        <ToastProvider>
          <PodcastPlayerProvider>
            {/* On a partner's own domain: the partner's branded app (src/partner/). */}
            {partnerSite ? <PartnerApp site={partnerSite} /> : <RouterProvider router={router} />}
            <PodcastPlayer />
            <GlobalUploadPanel />
            <AccountChangeGuard />
          </PodcastPlayerProvider>
        </ToastProvider>
      </HelmetProvider>
    </AppErrorBoundary>
  </React.StrictMode>
);
